import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Upload } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPerson,
  CampaignPlanTier,
} from "@shared-core/data/api-types";
import {
  ADDRESS_SOURCE_LABELS,
  consentNotes,
  formatTrail,
  notIncludedLabel,
} from "@shared-core/domain/direct-mail-campaigns";
import { paths } from "@shared-core/routing/routes";
import { ChannelIcon } from "../../app/components/domain/marketing/CampaignStepDisplay";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../app/components/primitives/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { failureLogFields } from "./actionErrors";

const ALL_TIERS = "all";
const PAGE_SIZE = 50;

/** Which mailings, sends and call rounds include this person, and which
 *  would reach them but leave them out, with the reason. */
function PersonSteps({ person }: { person: CampaignPerson }) {
  if (person.steps.length === 0) {
    return (
      <span className="text-muted-foreground">
        None yet – no step goes to the {person.tier_name} tier
      </span>
    );
  }
  return (
    <ul className="space-y-0.5">
      {person.steps.map((step) => (
        <li key={step.step_id} className="flex items-start gap-1.5">
          <ChannelIcon
            channel={step.channel}
            className="size-3.5 mt-0.5 shrink-0 text-muted-foreground"
          />
          {step.included ? (
            <span className="text-foreground">{step.label}</span>
          ) : (
            <span className="text-amber-800 dark:text-amber-300">
              {step.label}
              {step.skip_reason &&
                `: ${notIncludedLabel(step.channel, step.skip_reason)}`}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * "View people" on a campaign with tiers: everyone in its audience who has
 * a tier, with their address source, consent, which steps include them and
 * what has happened to them so far, filtered by tier and paged on the
 * server.
 */
export function CampaignPeopleSheet({
  campaignId,
  tiers,
  open,
  onOpenChange,
  refreshKey,
  onAddPeople,
}: {
  campaignId: string;
  tiers: CampaignPlanTier[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bumped by the page after an upload so the list reloads. */
  refreshKey: number;
  onAddPeople: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-5xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>People in this campaign</SheetTitle>
          <SheetDescription>
            Each person's tier, where their postal address comes from, what
            they've opted out of, which mailings, sends and call rounds include
            them, and what has happened so far.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">
          {open && (
            <CampaignPeopleList
              campaignId={campaignId}
              tiers={tiers}
              refreshKey={refreshKey}
              onAddPeople={onAddPeople}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function CampaignPeopleList({
  campaignId,
  tiers,
  refreshKey,
  onAddPeople,
}: {
  campaignId: string;
  tiers: CampaignPlanTier[];
  refreshKey: number;
  onAddPeople: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [tierFilter, setTierFilter] = useState<string>(ALL_TIERS);
  const [page, setPage] = useState(1);
  const [people, setPeople] = useState<CampaignPerson[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setPeople(null);
    try {
      const res = await dataAdapter.listCampaignPeople(campaignId, {
        tierId: tierFilter === ALL_TIERS ? undefined : tierFilter,
        page,
        pageSize: PAGE_SIZE,
      });
      setPeople(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) {
      console.error(
        "marketing.campaign.people.load_failed",
        failureLogFields(e, { campaign_id: campaignId }),
      );
      setError(true);
    }
  }, [dataAdapter, campaignId, tierFilter, page]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  function changeTier(value: string) {
    setTierFilter(value);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label
            htmlFor="campaign-people-tier"
            className="text-sm text-muted-foreground"
          >
            Tier
          </label>
          <Select value={tierFilter} onValueChange={changeTier}>
            <SelectTrigger id="campaign-people-tier" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TIERS}>All tiers</SelectItem>
              {tiers.map((tier) => (
                <SelectItem key={tier.id} value={tier.id}>
                  {tier.name} ({tier.person_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {people && (
            <span className="text-sm text-muted-foreground">
              {total} {total === 1 ? "person" : "people"}
            </span>
          )}
        </div>
        <Button onClick={onAddPeople}>
          <Upload className="size-4" />
          Add people and tiers
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-border p-6 text-center text-sm">
          <p className="text-red-700 dark:text-red-300">
            Couldn't load the people in this campaign.
          </p>
          <Button variant="outline" className="mt-3" onClick={() => load()}>
            Try again
          </Button>
        </div>
      )}

      {!error && people === null && (
        <p className="text-sm text-muted-foreground">Loading people…</p>
      )}

      {!error && people?.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {tierFilter === ALL_TIERS
            ? "Nobody has a tier in this campaign yet. Upload a list with a tier column to set them."
            : "No one is in this tier yet."}
        </div>
      )}

      {!error && people && people.length > 0 && (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Steps that include them</TableHead>
                <TableHead>What's happened</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {people.map((person) => {
                const notes = consentNotes(person.consent);
                return (
                  <TableRow key={person.person_id}>
                    <TableCell className="align-top">
                      <Link
                        to={paths.marketingPersonDetail({
                          personId: person.person_id,
                        })}
                        className="font-medium text-foreground hover:text-primary hover:underline"
                      >
                        {person.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {[person.job_title, person.company_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      {person.is_inactive && (
                        <div className="text-xs text-amber-800 dark:text-amber-300">
                          Left the company
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="outline">{person.tier_name}</Badge>
                    </TableCell>
                    <TableCell className="align-top whitespace-normal">
                      <div
                        className={
                          person.address_source === "none"
                            ? "text-red-700 dark:text-red-300 font-medium"
                            : "text-foreground"
                        }
                      >
                        {ADDRESS_SOURCE_LABELS[person.address_source]}
                      </div>
                      {person.postal_address && (
                        <div className="text-xs text-muted-foreground max-w-56">
                          {person.postal_address}
                        </div>
                      )}
                      {person.address_returned_at && (
                        <div className="text-xs text-amber-800 dark:text-amber-300">
                          Returned post – address needs fixing
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top whitespace-normal">
                      {notes.length === 0 ? (
                        <span className="text-muted-foreground">
                          OK for post, email and phone
                        </span>
                      ) : (
                        notes.map((note) => (
                          <div
                            key={note}
                            className="text-amber-800 dark:text-amber-300"
                          >
                            {note}
                          </div>
                        ))
                      )}
                    </TableCell>
                    <TableCell className="align-top whitespace-normal text-sm min-w-56">
                      <PersonSteps person={person} />
                    </TableCell>
                    <TableCell className="align-top whitespace-normal text-sm text-foreground max-w-80">
                      {formatTrail(person.trail)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!error && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1 || people === null}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages || people === null}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
